/**
 * The three steps of a funding analyser, as a progress strip — one component for every module.
 *
 * WHY THREE STEPS (owner's review with an actuary, September 2026: "this could be daunting to a
 * consumer — advisers would probably be ok"). An analyser used to arrive all at once: the inputs,
 * the position, the mode chooser and a fourteen-figure plan summary, before the client had done
 * anything. Nothing was wrong with any of it; it was the order. A client has three questions, in
 * order — what does this cost, what does that do to us, and what can we do about it — and the
 * strip puts the screen in that order. Every step is reachable at any time; the strip is a map,
 * not a gate.
 *
 * Built for Aged Care on 22 Sep 2026 and lifted here on 24 Sep so the Retirement Village module
 * (and the others after it) walk the same spine with the same strip (owner: "try to get
 * harmonisation of the UI/UX"). The steps are numbered because they ARE a sequence: the second
 * cannot be read before the first has its figure, and the third is measured against the second.
 */
import React from 'react';
import { Check, ChevronRight } from 'lucide-react';

export type AnalyserStep = 1 | 2 | 3;

export interface AnalyserStepDef {
    title: string;
    sub: string;
}

interface AnalyserStepsProps {
    step: AnalyserStep;
    onStep: (step: AnalyserStep) => void;
    /** Exactly three, in order. */
    steps: readonly [AnalyserStepDef, AnalyserStepDef, AnalyserStepDef];
    ariaLabel?: string;
}

const AnalyserSteps = ({ step, onStep, steps, ariaLabel = 'Analyser steps' }: AnalyserStepsProps) => (
    <nav aria-label={ariaLabel} className="mt-6 flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
        {steps.map((s, i) => {
            const n = (i + 1) as AnalyserStep;
            const active = n === step;
            const done = n < step;
            return (
                <React.Fragment key={n}>
                    <button
                        type="button"
                        onClick={() => onStep(n)}
                        aria-current={active ? 'step' : undefined}
                        className={`flex flex-1 items-center gap-3 rounded-xl border-2 px-3.5 py-2.5 text-left transition-colors ${
                            active
                                ? 'border-blue-600 bg-blue-50 text-[#001E7A] shadow-sm'
                                : done
                                    ? 'border-emerald-200 bg-white text-slate-800 hover:border-blue-300'
                                    : 'border-slate-200 bg-white text-slate-500 hover:border-blue-300'
                        }`}
                    >
                        <span
                            className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[13px] font-bold ${
                                active ? 'bg-blue-600 text-white' : done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'
                            }`}
                            aria-hidden
                        >
                            {done ? <Check size={15} strokeWidth={3} /> : n}
                        </span>
                        <span className="flex min-w-0 flex-col leading-tight">
                            <span className="text-[14px] font-bold">{s.title}</span>
                            <span className="text-[13px] text-slate-500">{s.sub}</span>
                        </span>
                    </button>
                    {i < steps.length - 1 && <ChevronRight size={18} className="hidden shrink-0 text-slate-400 md:block" aria-hidden />}
                </React.Fragment>
            );
        })}
    </nav>
);

export default AnalyserSteps;
